import type { Task } from "../types/task";
import { topicService } from "./topicService";
import { projectService } from "./projectService";
import {
  TimePeriod,
  CompletionStatus,
  OverrunThreshold,
  TimeDeviationCategory,
} from "../types/enums";

export interface FilterSettings {
  selectedPeriod: string;
  dateRange: { start: string; end: string };
  selectedTopics: string[];
  selectedProjects?: string[];
  selectedCompletionStatus: string;
  filterBySessions: boolean;
}

export interface TimeManagementInsights {
  averageTimeDeviation: number;
  previousAverageTimeDeviation?: number;
  mostAccurateTopics: Array<{
    topic: string;
    timeDeviation: number;
    taskCount: number;
  }>;
  timeOfDayPatterns: Array<{
    hour: number;
    productivity: number;
    taskCount: number;
  }>;
  sessionLengthAnalysis: {
    averageSessionLength: number;
    previousAverageSessionLength?: number;
    optimalSessionLength: number | null;
    previousOptimalSessionLength?: number | null;
    sessionLengthDistribution: Array<{
      range: string;
      count: number;
      percentage: number;
    }>;
  };
}

export interface CompletionMetrics {
  totalTasks: number;
  completedTasks: number;
  completionRate: number;
  totalDuration: number;
  totalEstimated: number;
  timeDeviationByCategory: {
    perfect: number; // ≤105%
    great: number; // 105-110%
    good: number; // 110-120%
    overrun: number; // >120%
  };
}

export interface TopicAnalysis {
  topics: Array<{
    name: string;
    totalTasks: number;
    completedTasks: number;
    averageDuration: number;
    averageEstimate: number;
    timeDeviation: number;
    overrunRate: number;
    totalDuration: number;
  }>;
  mostOverrunTopics: Array<{ topic: string; overrunRate: number }>;
  durationByTopic: Array<{
    topic: string;
    totalDuration: number;
    percentage: number;
  }>;
}

export interface OverrunAnalysis {
  totalOverruns: number;
  overrunRate: number;
  reasons: Array<{
    reason: string;
    count: number;
    percentage: number;
    averageOverrun: number;
  }>;
  mostCommonReasons: string[];
}

export interface DailyDuration {
  dailyData: Array<{
    date: string;
    totalDuration: number;
    sessionCount: number;
    percentage: number;
  }>;
}

class AnalyticsService {
  /**
   * Calculate time management insights based on filtered tasks
   */
  calculateTimeManagementInsights(
    tasks: Task[],
    filters: FilterSettings
  ): TimeManagementInsights {
    // Get current period metrics
    const currentMetrics = this.getTimeManagementMetrics(tasks, filters);

    // Get previous period metrics for comparison
    const previousFilters = this.getPreviousPeriodFilters(filters);
    const previousMetrics = previousFilters
      ? this.getTimeManagementMetrics(tasks, previousFilters)
      : null;

    return {
      averageTimeDeviation: currentMetrics.averageTimeDeviation,
      previousAverageTimeDeviation: previousMetrics?.averageTimeDeviation,
      mostAccurateTopics: currentMetrics.mostAccurateTopics,
      timeOfDayPatterns: currentMetrics.timeOfDayPatterns,
      sessionLengthAnalysis: {
        ...currentMetrics.sessionLengthAnalysis,
        previousAverageSessionLength:
          previousMetrics?.sessionLengthAnalysis.averageSessionLength,
        previousOptimalSessionLength:
          previousMetrics?.sessionLengthAnalysis.optimalSessionLength,
      },
    };
  }

  /**
   * Get time management metrics for a given set of filters
   * This is the core analytics endpoint that can be called with different filters
   */
  private getTimeManagementMetrics(
    tasks: Task[],
    filters: FilterSettings
  ): {
    averageTimeDeviation: number;
    mostAccurateTopics: Array<{
      topic: string;
      timeDeviation: number;
      taskCount: number;
    }>;
    timeOfDayPatterns: Array<{
      hour: number;
      productivity: number;
      taskCount: number;
    }>;
    sessionLengthAnalysis: {
      averageSessionLength: number;
      optimalSessionLength: number | null;
      sessionLengthDistribution: Array<{
        range: string;
        count: number;
        percentage: number;
      }>;
    };
  } {
    const filteredTasks = this.filterTasks(tasks, filters);
    const completedTasks = filteredTasks.filter(
      (task) => task.completed && task.estimatedDuration > 0
    );

    // Only return metrics if there's sufficient data (at least 3 completed tasks)
    if (completedTasks.length < 3) {
      return {
        averageTimeDeviation: 0,
        mostAccurateTopics: [],
        timeOfDayPatterns: [],
        sessionLengthAnalysis: {
          averageSessionLength: 0,
          optimalSessionLength: null,
          sessionLengthDistribution: [],
        },
      };
    }

    return {
      averageTimeDeviation: this.calculateAverageTimeDeviation(completedTasks),
      mostAccurateTopics: this.calculateMostAccurateTopics(completedTasks),
      timeOfDayPatterns: this.calculateTimeOfDayPatterns(filteredTasks),
      sessionLengthAnalysis: this.calculateSessionLengthAnalysis(filteredTasks),
    };
  }

  /**
   * Calculate completion metrics
   */
  calculateCompletionMetrics(
    tasks: Task[],
    filters: FilterSettings
  ): CompletionMetrics {
    const filteredTasks = this.filterTasks(tasks, filters);
    const completedTasks = filteredTasks.filter((task) => task.completed);
    const tasksWithEstimates = completedTasks.filter(
      (task) => task.estimatedDuration > 0
    );

    const totalDuration = completedTasks.reduce(
      (sum, task) => sum + task.duration,
      0
    );
    const totalEstimated = tasksWithEstimates.reduce(
      (sum, task) => sum + task.estimatedDuration,
      0
    );

    return {
      totalTasks: filteredTasks.length,
      completedTasks: completedTasks.length,
      completionRate:
        filteredTasks.length > 0
          ? (completedTasks.length / filteredTasks.length) * 100
          : 0,
      totalDuration,
      totalEstimated,
      timeDeviationByCategory:
        this.calculateTimeDeviationByCategory(tasksWithEstimates),
    };
  }

  /**
   * Calculate topic analysis
   */
  calculateTopicAnalysis(
    tasks: Task[],
    filters: FilterSettings
  ): TopicAnalysis {
    const filteredTasks = this.filterTasks(tasks, filters);
    const topicMap = new Map<string, { tasks: Task[]; completed: Task[] }>();

    // Group tasks by topic
    filteredTasks.forEach((task) => {
      const topic = topicService.getTopicById(task.topicId)?.name || "Other";
      if (!topicMap.has(topic)) {
        topicMap.set(topic, { tasks: [], completed: [] });
      }
      topicMap.get(topic)!.tasks.push(task);
      if (task.completed) {
        topicMap.get(topic)!.completed.push(task);
      }
    });

    const topics = Array.from(topicMap.entries()).map(([topicName, data]) => {
      const completedWithEstimates = data.completed.filter(
        (task) => task.estimatedDuration > 0
      );
      const totalDuration = data.completed.reduce(
        (sum, task) => sum + task.duration,
        0
      );
      const averageDuration =
        data.completed.length > 0 ? totalDuration / data.completed.length : 0;
      const averageEstimate =
        completedWithEstimates.length > 0
          ? completedWithEstimates.reduce(
              (sum, task) => sum + task.estimatedDuration,
              0
            ) / completedWithEstimates.length
          : 0;
      const timeDeviation = this.calculateAverageTimeDeviation(
        completedWithEstimates
      );
      const overrunRate =
        completedWithEstimates.length > 0
          ? completedWithEstimates.reduce((sum, task) => {
              const taskOverrun =
                (task.duration / task.estimatedDuration - 1) * 100;
              return sum + Math.max(0, taskOverrun); // Only count positive overruns
            }, 0) / completedWithEstimates.length
          : 0;

      return {
        name: topicName,
        totalTasks: data.tasks.length,
        completedTasks: data.completed.length,
        averageDuration,
        averageEstimate,
        timeDeviation,
        overrunRate,
        totalDuration,
      };
    });

    const mostOverrunTopics = topics
      .filter((topic) => topic.completedTasks > 0)
      .sort((a, b) => b.overrunRate - a.overrunRate)
      .slice(0, 5)
      .map((topic) => ({ topic: topic.name, overrunRate: topic.overrunRate }));

    // Calculate duration by topic for chart
    const totalDuration = topics.reduce(
      (sum, topic) => sum + topic.totalDuration,
      0
    );
    const durationByTopic = topics
      .filter((topic) => topic.totalDuration > 0)
      .sort((a, b) => b.totalDuration - a.totalDuration)
      .map((topic) => ({
        topic: topic.name,
        totalDuration: topic.totalDuration,
        percentage:
          totalDuration > 0 ? (topic.totalDuration / totalDuration) * 100 : 0,
      }));

    return { topics, mostOverrunTopics, durationByTopic };
  }

  /**
   * Calculate project analysis
   */
  calculateProjectAnalysis(
    tasks: Task[],
    filters: FilterSettings
  ): TopicAnalysis {
    const filteredTasks = this.filterTasks(tasks, filters);
    const projectMap = new Map<string, { tasks: Task[]; completed: Task[] }>();

    // Group tasks by project
    filteredTasks.forEach((task) => {
      if (!task.projectId) return; // Skip tasks without projects
      const project = projectService.getProjectById(task.projectId);
      const projectName = project?.name || "Unknown";
      if (!projectMap.has(projectName)) {
        projectMap.set(projectName, { tasks: [], completed: [] });
      }
      projectMap.get(projectName)!.tasks.push(task);
      if (task.completed) {
        projectMap.get(projectName)!.completed.push(task);
      }
    });

    const projects = Array.from(projectMap.entries()).map(([projectName, data]) => {
      const completedWithEstimates = data.completed.filter(
        (task) => task.estimatedDuration > 0
      );
      const totalDuration = data.completed.reduce(
        (sum, task) => sum + task.duration,
        0
      );
      const averageDuration =
        data.completed.length > 0 ? totalDuration / data.completed.length : 0;
      const averageEstimate =
        completedWithEstimates.length > 0
          ? completedWithEstimates.reduce(
              (sum, task) => sum + task.estimatedDuration,
              0
            ) / completedWithEstimates.length
          : 0;
      const timeDeviation = this.calculateAverageTimeDeviation(
        completedWithEstimates
      );
      const overrunRate =
        completedWithEstimates.length > 0
          ? completedWithEstimates.reduce((sum, task) => {
              const taskOverrun =
                (task.duration / task.estimatedDuration - 1) * 100;
              return sum + Math.max(0, taskOverrun); // Only count positive overruns
            }, 0) / completedWithEstimates.length
          : 0;

      return {
        name: projectName,
        totalTasks: data.tasks.length,
        completedTasks: data.completed.length,
        averageDuration,
        averageEstimate,
        timeDeviation,
        overrunRate,
        totalDuration,
      };
    });

    const mostOverrunProjects = projects
      .filter((project) => project.completedTasks > 0)
      .sort((a, b) => b.overrunRate - a.overrunRate)
      .slice(0, 5)
      .map((project) => ({
        topic: project.name,
        overrunRate: project.overrunRate,
      }));

    // Calculate duration by project for chart
    const totalDuration = projects.reduce(
      (sum, project) => sum + project.totalDuration,
      0
    );
    const durationByProject = projects
      .filter((project) => project.totalDuration > 0)
      .sort((a, b) => b.totalDuration - a.totalDuration)
      .map((project) => ({
        topic: project.name,
        totalDuration: project.totalDuration,
        percentage:
          totalDuration > 0 ? (project.totalDuration / totalDuration) * 100 : 0,
      }));

    return { topics: projects, mostOverrunTopics: mostOverrunProjects, durationByTopic: durationByProject };
  }

  /**
   * Calculate daily duration analysis by processing individual sessions
   */
  calculateDailyDuration(
    tasks: Task[],
    filters: FilterSettings
  ): DailyDuration {
    const filteredTasks = this.filterTasks(tasks, filters);
    const dailyMap = new Map<
      string,
      { totalDuration: number; sessionCount: number }
    >();

    // Process each task's sessions
    filteredTasks.forEach((task) => {
      if (!task.sessions || task.sessions.length === 0) return;

      task.sessions.forEach((session) => {
        if (!session.endTime) return; // Skip incomplete sessions

        // Check if this specific session passes the period filter
        if (!this.isSessionInPeriod(session, filters)) return;

        const startTime = new Date(session.startTime);
        const endTime = new Date(session.endTime);

        // Split session across days if it spans multiple days
        const sessionSegments = this.splitSessionAcrossDays(startTime, endTime);

        sessionSegments.forEach((segment) => {
          const dateKey = segment.date; // YYYY-MM-DD format

          if (!dailyMap.has(dateKey)) {
            dailyMap.set(dateKey, { totalDuration: 0, sessionCount: 0 });
          }

          const dayData = dailyMap.get(dateKey)!;
          dayData.totalDuration += segment.duration;
          dayData.sessionCount += 1;
        });
      });
    });

    // Convert to array and sort by date
    const dailyEntries = Array.from(dailyMap.entries()).sort(([a], [b]) =>
      a.localeCompare(b)
    );

    // Calculate total duration for percentage calculation
    const totalDuration = dailyEntries.reduce(
      (sum, [, data]) => sum + data.totalDuration,
      0
    );

    // Format the data with percentages
    const dailyData = dailyEntries.map(([date, data]) => ({
      date,
      totalDuration: data.totalDuration,
      sessionCount: data.sessionCount,
      percentage:
        totalDuration > 0 ? (data.totalDuration / totalDuration) * 100 : 0,
    }));

    return { dailyData };
  }

  /**
   * Check if a specific session falls within the selected period
   */
  private isSessionInPeriod(session: any, filters: FilterSettings): boolean {
    const sessionDate = new Date(session.startTime);
    const sessionDateOnly = new Date(
      sessionDate.getFullYear(),
      sessionDate.getMonth(),
      sessionDate.getDate()
    );
    const today = new Date();
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    switch (filters.selectedPeriod) {
      case TimePeriod.ALL:
        return true;
      case TimePeriod.TODAY:
        return sessionDateOnly.getTime() === todayOnly.getTime();
      case TimePeriod.WEEK: {
        const startOfWeek = new Date();
        const dayOfWeek = startOfWeek.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        // Calculate days to subtract to get to Monday (1)
        const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const startOfWeekDate = new Date(startOfWeek);
        startOfWeekDate.setDate(startOfWeek.getDate() - daysToSubtract);
        startOfWeekDate.setHours(0, 0, 0, 0);

        const endOfWeekDate = new Date(startOfWeekDate);
        endOfWeekDate.setDate(startOfWeekDate.getDate() + 6);
        endOfWeekDate.setHours(23, 59, 59, 999);

        return (
          sessionDateOnly >= startOfWeekDate && sessionDateOnly <= endOfWeekDate
        );
      }
      case TimePeriod.MONTH: {
        const startOfMonth = new Date(
          todayOnly.getFullYear(),
          todayOnly.getMonth(),
          1
        );
        startOfMonth.setHours(0, 0, 0, 0);

        const endOfMonth = new Date(
          todayOnly.getFullYear(),
          todayOnly.getMonth() + 1,
          0
        );
        endOfMonth.setHours(23, 59, 59, 999);

        return sessionDateOnly >= startOfMonth && sessionDateOnly <= endOfMonth;
      }
      case TimePeriod.YEAR: {
        const firstDayOfYear = new Date(todayOnly.getFullYear(), 0, 1);
        return (
          sessionDateOnly >= firstDayOfYear && sessionDateOnly <= todayOnly
        );
      }
      case TimePeriod.CUSTOM: {
        if (!filters.dateRange.start || !filters.dateRange.end) {
          return true;
        } else {
          const startDate = new Date(filters.dateRange.start);
          const endDate = new Date(filters.dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          startDate.setHours(0, 0, 0, 0);
          return sessionDateOnly >= startDate && sessionDateOnly <= endDate;
        }
      }
      default:
        return true;
    }
  }

  /**
   * Split a session across multiple days if it spans midnight
   */
  private splitSessionAcrossDays(
    startTime: Date,
    endTime: Date
  ): Array<{ date: string; duration: number }> {
    const segments: Array<{ date: string; duration: number }> = [];

    let currentStart = new Date(startTime);
    let currentEnd = new Date(endTime);

    while (currentStart < currentEnd) {
      // Get the end of the current day (23:59:59.999)
      const endOfDay = new Date(
        currentStart.getFullYear(),
        currentStart.getMonth(),
        currentStart.getDate(),
        23,
        59,
        59,
        999
      );

      // Determine the end time for this segment
      const segmentEnd = currentEnd < endOfDay ? currentEnd : endOfDay;

      // Calculate duration for this segment
      const segmentDuration = segmentEnd.getTime() - currentStart.getTime();

      if (segmentDuration > 0) {
        const dateKey = currentStart.toISOString().split("T")[0]; // YYYY-MM-DD format
        segments.push({
          date: dateKey,
          duration: segmentDuration,
        });
      }

      // Move to the next day
      currentStart = new Date(
        currentStart.getFullYear(),
        currentStart.getMonth(),
        currentStart.getDate() + 1,
        0,
        0,
        0,
        0
      );
    }

    return segments;
  }

  /**
   * Calculate overrun analysis
   */
  calculateOverrunAnalysis(
    tasks: Task[],
    filters: FilterSettings
  ): OverrunAnalysis {
    const filteredTasks = this.filterTasks(tasks, filters);
    const overrunTasks = filteredTasks.filter(
      (task) =>
        task.completed &&
        task.estimatedDuration > 0 &&
        task.duration / task.estimatedDuration > OverrunThreshold.PERCENTAGE
    );

    const reasonMap = new Map<
      string,
      { count: number; totalOverrun: number }
    >();

    overrunTasks.forEach((task) => {
      if (task.overrunReasons && task.overrunReasons.length > 0) {
        task.overrunReasons.forEach((reasonId) => {
          const reason = this.getOverrunReasonLabel(reasonId);
          if (!reasonMap.has(reason)) {
            reasonMap.set(reason, { count: 0, totalOverrun: 0 });
          }
          const data = reasonMap.get(reason)!;
          data.count++;
          data.totalOverrun += task.duration / task.estimatedDuration - 1;
        });
      }
    });

    const reasons = Array.from(reasonMap.entries())
      .map(([reason, data]) => ({
        reason,
        count: data.count,
        percentage: (data.count / overrunTasks.length) * 100,
        averageOverrun: (data.totalOverrun / data.count) * 100,
      }))
      .sort((a, b) => b.count - a.count);

    const mostCommonReasons = reasons.slice(0, 3).map((r) => r.reason);

    return {
      totalOverruns: overrunTasks.length,
      overrunRate:
        filteredTasks.filter(
          (task) => task.completed && task.estimatedDuration > 0
        ).length > 0
          ? (overrunTasks.length /
              filteredTasks.filter(
                (task) => task.completed && task.estimatedDuration > 0
              ).length) *
            100
          : 0,
      reasons,
      mostCommonReasons,
    };
  }

  /**
   * Get filter settings for the previous period
   */
  private getPreviousPeriodFilters(
    filters: FilterSettings
  ): FilterSettings | null {
    const today = new Date();
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    switch (filters.selectedPeriod) {
      case TimePeriod.TODAY: {
        const yesterday = new Date(todayOnly);
        yesterday.setDate(todayOnly.getDate() - 1);
        return {
          ...filters,
          selectedPeriod: TimePeriod.CUSTOM,
          dateRange: {
            start: yesterday.toISOString().split("T")[0],
            end: yesterday.toISOString().split("T")[0],
          },
        };
      }
      case TimePeriod.WEEK: {
        const lastWeekStart = new Date(todayOnly);
        lastWeekStart.setDate(todayOnly.getDate() - 14);
        const lastWeekEnd = new Date(todayOnly);
        lastWeekEnd.setDate(todayOnly.getDate() - 8);
        return {
          ...filters,
          selectedPeriod: TimePeriod.CUSTOM,
          dateRange: {
            start: lastWeekStart.toISOString().split("T")[0],
            end: lastWeekEnd.toISOString().split("T")[0],
          },
        };
      }
      case TimePeriod.MONTH: {
        const lastMonthStart = new Date(
          todayOnly.getFullYear(),
          todayOnly.getMonth() - 1,
          1
        );
        const lastMonthEnd = new Date(
          todayOnly.getFullYear(),
          todayOnly.getMonth(),
          0
        );
        return {
          ...filters,
          selectedPeriod: TimePeriod.CUSTOM,
          dateRange: {
            start: lastMonthStart.toISOString().split("T")[0],
            end: lastMonthEnd.toISOString().split("T")[0],
          },
        };
      }
      case TimePeriod.YEAR: {
        const lastYearStart = new Date(todayOnly.getFullYear() - 1, 0, 1);
        const lastYearEnd = new Date(todayOnly.getFullYear() - 1, 11, 31);
        return {
          ...filters,
          selectedPeriod: TimePeriod.CUSTOM,
          dateRange: {
            start: lastYearStart.toISOString().split("T")[0],
            end: lastYearEnd.toISOString().split("T")[0],
          },
        };
      }
      case TimePeriod.CUSTOM: {
        if (!filters.dateRange.start || !filters.dateRange.end) return null;

        const startDate = new Date(filters.dateRange.start);
        const endDate = new Date(filters.dateRange.end);
        const daysDiff = Math.ceil(
          (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        const previousStartDate = new Date(startDate);
        previousStartDate.setDate(startDate.getDate() - daysDiff);
        const previousEndDate = new Date(startDate);
        previousEndDate.setDate(startDate.getDate() - 1);

        return {
          ...filters,
          dateRange: {
            start: previousStartDate.toISOString().split("T")[0],
            end: previousEndDate.toISOString().split("T")[0],
          },
        };
      }
      default:
        return null;
    }
  }

  /**
   * Private helper methods
   */
  private filterTasks(tasks: Task[], filters: FilterSettings): Task[] {
    let filtered = tasks;

    // Apply topic filter
    if (filters.selectedTopics.length > 0) {
      filtered = filtered.filter((task) =>
        filters.selectedTopics.includes(task.topicId)
      );
    }

    // Apply project filter
    if (filters.selectedProjects && filters.selectedProjects.length > 0) {
      filtered = filtered.filter((task) => {
        // Only include tasks with a projectId that matches the filter
        if (!task.projectId) {
          return false; // Exclude tasks without projects when filtering by projects
        }
        return filters.selectedProjects!.includes(task.projectId);
      });
    }

    // Apply completion status filter
    if (filters.selectedCompletionStatus === CompletionStatus.COMPLETED) {
      filtered = filtered.filter((task) => task.completed);
    } else if (
      filters.selectedCompletionStatus === CompletionStatus.NOT_COMPLETED
    ) {
      filtered = filtered.filter((task) => !task.completed);
    } else if (filters.selectedCompletionStatus === CompletionStatus.ALL) {
      // Show all tasks regardless of completion status
    }
    // If filters.selectedCompletionStatus is empty or any other value, show all tasks

    // Apply time period filter
    filtered = filtered.filter((task) => this.isTaskInPeriod(task, filters));

    return filtered;
  }

  private isTaskInPeriod(task: Task, filters: FilterSettings): boolean {
    if (filters.filterBySessions && task.sessions && task.sessions.length > 0) {
      // Filter by any session - check if ANY session falls within the period (matches TimeTracker logic)
      const today = new Date();
      const todayOnly = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );

      // Check if any session falls within the selected period
      return task.sessions.some((session) => {
        const sessionDate = new Date(session.startTime);
        const sessionDateOnly = new Date(
          sessionDate.getFullYear(),
          sessionDate.getMonth(),
          sessionDate.getDate()
        );

        switch (filters.selectedPeriod) {
          case TimePeriod.ALL:
            return true;
          case TimePeriod.TODAY:
            return sessionDateOnly.getTime() === todayOnly.getTime();
          case TimePeriod.WEEK: {
            const startOfWeek = new Date();
            const dayOfWeek = startOfWeek.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

            // Calculate days to subtract to get to Monday (1)
            const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            const startOfWeekDate = new Date(startOfWeek);
            startOfWeekDate.setDate(startOfWeek.getDate() - daysToSubtract);
            startOfWeekDate.setHours(0, 0, 0, 0);

            const endOfWeekDate = new Date(startOfWeekDate);
            endOfWeekDate.setDate(startOfWeekDate.getDate() + 6);
            endOfWeekDate.setHours(23, 59, 59, 999);

            return (
              sessionDateOnly >= startOfWeekDate &&
              sessionDateOnly <= endOfWeekDate
            );
          }
          case TimePeriod.MONTH: {
            const startOfMonth = new Date(
              todayOnly.getFullYear(),
              todayOnly.getMonth(),
              1
            );
            startOfMonth.setHours(0, 0, 0, 0);

            const endOfMonth = new Date(
              todayOnly.getFullYear(),
              todayOnly.getMonth() + 1,
              0
            );
            endOfMonth.setHours(23, 59, 59, 999);

            return (
              sessionDateOnly >= startOfMonth && sessionDateOnly <= endOfMonth
            );
          }
          case TimePeriod.YEAR: {
            const firstDayOfYear = new Date(todayOnly.getFullYear(), 0, 1);
            return (
              sessionDateOnly >= firstDayOfYear && sessionDateOnly <= todayOnly
            );
          }
          case TimePeriod.CUSTOM: {
            if (!filters.dateRange.start || !filters.dateRange.end) {
              return true;
            } else {
              const startDate = new Date(filters.dateRange.start);
              const endDate = new Date(filters.dateRange.end);
              endDate.setHours(23, 59, 59, 999);
              startDate.setHours(0, 0, 0, 0);
              return sessionDateOnly >= startDate && sessionDateOnly <= endDate;
            }
          }
          default:
            return true;
        }
      });
    } else {
      // Fallback to task-based filtering when not filtering by sessions
      let taskDate: Date;

      if (task.sessions && task.sessions.length > 0) {
        taskDate = new Date(task.sessions[0].startTime);
      } else {
        taskDate = new Date(task.createdAt || new Date().toISOString());
      }

      const taskDateOnly = new Date(
        taskDate.getFullYear(),
        taskDate.getMonth(),
        taskDate.getDate()
      );
      const today = new Date();
      const todayOnly = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );

      switch (filters.selectedPeriod) {
        case TimePeriod.ALL:
          return true;
        case TimePeriod.TODAY:
          return taskDateOnly.getTime() === todayOnly.getTime();
        case TimePeriod.WEEK: {
          const startOfWeek = new Date();
          const dayOfWeek = startOfWeek.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

          // Calculate days to subtract to get to Monday (1)
          const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          const startOfWeekDate = new Date(startOfWeek);
          startOfWeekDate.setDate(startOfWeek.getDate() - daysToSubtract);
          startOfWeekDate.setHours(0, 0, 0, 0);

          const endOfWeekDate = new Date(startOfWeekDate);
          endOfWeekDate.setDate(startOfWeekDate.getDate() + 6);
          endOfWeekDate.setHours(23, 59, 59, 999);

          return (
            taskDateOnly >= startOfWeekDate && taskDateOnly <= endOfWeekDate
          );
        }
        case TimePeriod.MONTH: {
          const startOfMonth = new Date(
            todayOnly.getFullYear(),
            todayOnly.getMonth(),
            1
          );
          startOfMonth.setHours(0, 0, 0, 0);

          const endOfMonth = new Date(
            todayOnly.getFullYear(),
            todayOnly.getMonth() + 1,
            0
          );
          endOfMonth.setHours(23, 59, 59, 999);

          return taskDateOnly >= startOfMonth && taskDateOnly <= endOfMonth;
        }
        case TimePeriod.YEAR: {
          const firstDayOfYear = new Date(todayOnly.getFullYear(), 0, 1);
          return taskDateOnly >= firstDayOfYear && taskDateOnly <= todayOnly;
        }
        case TimePeriod.CUSTOM: {
          if (!filters.dateRange.start || !filters.dateRange.end) {
            return true;
          } else {
            const startDate = new Date(filters.dateRange.start);
            const endDate = new Date(filters.dateRange.end);
            endDate.setHours(23, 59, 59, 999);
            startDate.setHours(0, 0, 0, 0);
            return taskDateOnly >= startDate && taskDateOnly <= endDate;
          }
        }
        default:
          return true;
      }
    }
  }

  private calculateAverageTimeDeviation(tasks: Task[]): number {
    if (tasks.length === 0) return 0;

    const deviations = tasks.map(
      (task) => (task.duration / task.estimatedDuration) * 100
    );
    return (
      deviations.reduce((sum, deviation) => sum + deviation, 0) /
      deviations.length
    );
  }

  private calculateMostAccurateTopics(
    tasks: Task[]
  ): Array<{ topic: string; timeDeviation: number; taskCount: number }> {
    const topicMap = new Map<string, Task[]>();

    tasks.forEach((task) => {
      const topic = topicService.getTopicById(task.topicId)?.name || "Other";
      if (!topicMap.has(topic)) {
        topicMap.set(topic, []);
      }
      topicMap.get(topic)!.push(task);
    });

    return Array.from(topicMap.entries())
      .map(([topic, topicTasks]) => ({
        topic,
        timeDeviation: this.calculateAverageTimeDeviation(topicTasks),
        taskCount: topicTasks.length,
      }))
      .filter((item) => item.taskCount >= 2) // Only include topics with at least 2 tasks
      .sort((a, b) => a.timeDeviation - b.timeDeviation) // Sort by lowest deviation first (most accurate)
      .slice(0, 5);
  }

  private calculateTimeOfDayPatterns(
    tasks: Task[]
  ): Array<{ hour: number; productivity: number; taskCount: number }> {
    const hourMap = new Map<
      number,
      { totalDuration: number; taskCount: number }
    >();

    // Initialize all hours
    for (let hour = 0; hour < 24; hour++) {
      hourMap.set(hour, { totalDuration: 0, taskCount: 0 });
    }

    // Analyze sessions
    tasks.forEach((task) => {
      if (task.sessions) {
        task.sessions.forEach((session) => {
          const startHour = new Date(session.startTime).getHours();
          const data = hourMap.get(startHour)!;
          data.taskCount++;
          if (session.endTime) {
            const duration =
              new Date(session.endTime).getTime() -
              new Date(session.startTime).getTime();
            data.totalDuration += duration;
          }
        });
      }
    });

    return Array.from(hourMap.entries())
      .map(([hour, data]) => ({
        hour,
        productivity:
          data.taskCount > 0 ? data.totalDuration / data.taskCount : 0,
        taskCount: data.taskCount,
      }))
      .sort((a, b) => b.productivity - a.productivity);
  }

  private calculateSessionLengthAnalysis(tasks: Task[]): {
    averageSessionLength: number;
    optimalSessionLength: number | null;
    sessionLengthDistribution: Array<{
      range: string;
      count: number;
      percentage: number;
    }>;
  } {
    const sessions: number[] = [];

    tasks.forEach((task) => {
      if (task.sessions) {
        task.sessions.forEach((session) => {
          if (session.endTime) {
            const duration =
              new Date(session.endTime).getTime() -
              new Date(session.startTime).getTime();
            sessions.push(duration);
          }
        });
      }
    });

    if (sessions.length === 0) {
      return {
        averageSessionLength: 0,
        optimalSessionLength: null,
        sessionLengthDistribution: [],
      };
    }

    const averageSessionLength =
      sessions.reduce((sum, duration) => sum + duration, 0) / sessions.length;

    // Calculate truly "optimal" session length:
    // Select the session length associated with the best estimation accuracy.
    // Approach: For each completed task with an estimate, compute its median session length.
    // Bin tasks by that median session length, compute the mean absolute time deviation
    // from 100% in each bin, pick the bin with the lowest error (and >= minCount),
    // then return the median of task medians within that winning bin.
    const tasksEligibleForOptimal = tasks.filter(
      (task) =>
        task.completed &&
        task.estimatedDuration > 0 &&
        Array.isArray(task.sessions) &&
        task.sessions.some((s) => Boolean(s.endTime))
    );

    const perTaskMedianSession: Array<{
      medianSessionMs: number;
      deviationAbsPct: number;
    }> = [];

    const computeMedian = (values: number[]): number => {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    };

    tasksEligibleForOptimal.forEach((task) => {
      const sessionDurations = task.sessions
        .filter((s) => Boolean(s.endTime))
        .map(
          (s) =>
            new Date(s.endTime as string).getTime() -
            new Date(s.startTime).getTime()
        )
        .filter((d) => d > 0);
      if (sessionDurations.length === 0) return;

      const medianSessionMs = computeMedian(sessionDurations);
      const deviationAbsPct = Math.abs(
        (task.duration / task.estimatedDuration) * 100 - 100
      );
      perTaskMedianSession.push({ medianSessionMs, deviationAbsPct });
    });

    // Define bins identical to distribution ranges
    const optimalRanges = [
      { min: 0, max: 15 * 60 * 1000, label: "0-15 min" },
      { min: 15 * 60 * 1000, max: 30 * 60 * 1000, label: "15-30 min" },
      { min: 30 * 60 * 1000, max: 60 * 60 * 1000, label: "30-60 min" },
      { min: 60 * 60 * 1000, max: 120 * 60 * 1000, label: "1-2 hours" },
      { min: 120 * 60 * 1000, max: Infinity, label: "2+ hours" },
    ];

    const minTasksPerBin = 3;

    type BinStat = {
      label: string;
      count: number;
      avgAbsDeviation: number;
      medians: number[];
    };
    const binStats: BinStat[] = optimalRanges.map((r) => ({
      label: r.label,
      count: 0,
      avgAbsDeviation: Number.POSITIVE_INFINITY,
      medians: [],
    }));

    optimalRanges.forEach((range, idx) => {
      const inBin = perTaskMedianSession.filter(
        (p) => p.medianSessionMs >= range.min && p.medianSessionMs < range.max
      );
      if (inBin.length >= minTasksPerBin) {
        const avgAbsDeviation =
          inBin.reduce((sum, p) => sum + p.deviationAbsPct, 0) / inBin.length;
        binStats[idx].count = inBin.length;
        binStats[idx].avgAbsDeviation = avgAbsDeviation;
        binStats[idx].medians = inBin.map((p) => p.medianSessionMs);
      }
    });

    const bestBin = binStats
      .filter((b) => b.count >= minTasksPerBin)
      .sort((a, b) => {
        if (a.avgAbsDeviation !== b.avgAbsDeviation) {
          return a.avgAbsDeviation - b.avgAbsDeviation;
        }
        // tie-breaker: choose the bin with more data points
        return b.count - a.count;
      })[0];

    let optimalSessionLength: number | null;
    if (bestBin && bestBin.medians.length > 0) {
      // representative value: median of task median session lengths in the winning bin
      optimalSessionLength = computeMedian(bestBin.medians);
    } else {
      // Insufficient data to determine an optimal range
      optimalSessionLength = null;
    }

    // Session length distribution
    const ranges = [
      { min: 0, max: 15 * 60 * 1000, label: "0-15 min" },
      { min: 15 * 60 * 1000, max: 30 * 60 * 1000, label: "15-30 min" },
      { min: 30 * 60 * 1000, max: 60 * 60 * 1000, label: "30-60 min" },
      { min: 60 * 60 * 1000, max: 120 * 60 * 1000, label: "1-2 hours" },
      { min: 120 * 60 * 1000, max: Infinity, label: "2+ hours" },
    ];

    const distribution = ranges.map((range) => {
      const count = sessions.filter(
        (duration) => duration >= range.min && duration < range.max
      ).length;
      return {
        range: range.label,
        count,
        percentage: (count / sessions.length) * 100,
      };
    });

    return {
      averageSessionLength,
      optimalSessionLength,
      sessionLengthDistribution: distribution,
    };
  }

  private calculateTimeDeviationByCategory(tasks: Task[]): {
    perfect: number;
    great: number;
    good: number;
    overrun: number;
  } {
    const categories = { perfect: 0, great: 0, good: 0, overrun: 0 };

    tasks.forEach((task) => {
      const timeDeviation = (task.duration / task.estimatedDuration) * 100;
      if (timeDeviation <= TimeDeviationCategory.PERFECT * 100)
        categories.perfect++;
      else if (timeDeviation <= TimeDeviationCategory.GREAT * 100)
        categories.great++;
      else if (timeDeviation <= TimeDeviationCategory.GOOD * 100)
        categories.good++;
      else categories.overrun++;
    });

    return categories;
  }

  private getOverrunReasonLabel(reasonId: string): string {
    // This would ideally come from the overrunReasonService
    const reasonMap: Record<string, string> = {
      "increased-scope": "Increased scope",
      "more-complex": "More complex",
      "changes-requested": "Changes requested",
      blocked: "Blocked",
      other: "Other",
    };
    return reasonMap[reasonId] || reasonId;
  }
}

export const analyticsService = new AnalyticsService();
